import React, { useMemo, useState } from "react";
import {
  Box,
  PropertyFilter,
  PropertyFilterProps,
  SpaceBetween,
  StatusIndicator,
  Table,
  TableProps,
} from "@amzn/awsui-components-react";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  groupShadowTestItemsByGti,
  ShadowTestGtiGroup,
} from "../../helpers/shadowTestBundleUtils";

interface BundleTableRow<T> {
  id: string;
  kind: "group" | "item";
  item?: T;
  group?: ShadowTestGtiGroup<T>;
  children: BundleTableRow<T>[];
}

export interface BundleTableItem {
  gti?: string;
  passed?: boolean;
  usecase?: string;
}

// Wraps a table so it scrolls horizontally instead of being clipped. The line-table columns carry no
// fixed width, so Cloudscape auto-sizes each to its content; when the total exceeds the modal width
// this lets the user scroll sideways to reach the remaining columns.
const HorizontalScroll = ({ children }: { children: React.ReactNode }) => (
  <div style={{ overflowX: "auto", width: "100%" }}>{children}</div>
);

const createBundleRows = <T,>(
  groups: ShadowTestGtiGroup<T>[]
): BundleTableRow<T>[] =>
  groups.map((group, groupIndex) => {
    const groupId = `group-${group.gti || "unassigned"}-${groupIndex}`;
    return {
      id: groupId,
      kind: "group",
      group,
      children: group.items.map((item, itemIndex) => ({
        id: `${groupId}-item-${itemIndex}`,
        kind: "item",
        item,
        children: [],
      })),
    };
  });

// Build a comparator for the underlying items from a column's sorting metadata. Mirrors how
// Cloudscape/collection-hooks sorts: prefer an explicit comparator, otherwise compare the
// sortingField values as locale-aware strings. Returns null when the column is not sortable.
const columnItemComparator = <T,>(
  column: TableProps.ColumnDefinition<T>
): ((a: T, b: T) => number) | null => {
  if (column.sortingComparator) {
    return column.sortingComparator;
  }
  if (column.sortingField) {
    const field = column.sortingField as keyof T;
    return (a, b) =>
      String(a[field] ?? "").localeCompare(String(b[field] ?? ""));
  }
  return null;
};

// PropertyFilter i18n. Shared across every filterable table in the modal.
const FILTER_I18N_STRINGS: PropertyFilterProps.I18nStrings = {
  filteringAriaLabel: "Filter rows",
  filteringPlaceholder: "Filter rows by column",
  operationAndText: "and",
  operationOrText: "or",
  operatorsText: "Operators",
  operatorContainsText: "Contains",
  operatorDoesNotContainText: "Does not contain",
  operatorEqualsText: "Equals",
  operatorDoesNotEqualText: "Does not equal",
  propertyText: "Property",
  operatorText: "Operator",
  valueText: "Value",
  cancelActionText: "Cancel",
  applyActionText: "Apply",
  clearFiltersText: "Clear filters",
  groupValuesText: "Values",
  groupPropertiesText: "Properties",
  tokenLimitShowMore: "Show more",
  tokenLimitShowFewer: "Show fewer",
  editTokenHeader: "Edit filter",
  removeTokenButtonAriaLabel: (token) =>
    `Remove filter ${token.propertyLabel} ${token.operator} ${token.value}`,
  dismissAriaLabel: "Dismiss",
  enteredTextLabel: (text: string) => `Use: "${text}"`,
};

// Every column that is sortable is also filterable. String columns filter with contains/equals;
// the two amount columns are stored as strings too, so they use the same string operators.
const createFilteringProperties = <T,>(
  columnDefinitions: TableProps.ColumnDefinition<T>[]
): PropertyFilterProps.FilteringProperty[] =>
  columnDefinitions
    .filter((column) => column.sortingField)
    .map((column) => ({
      key: column.sortingField as string,
      propertyLabel:
        typeof column.header === "string" ? column.header : String(column.id),
      groupValuesLabel: `${
        typeof column.header === "string" ? column.header : String(column.id)
      } values`,
      operators: [":", "!:", "=", "!="],
    }));

const createBundleColumns = <T,>(
  columnDefinitions: TableProps.ColumnDefinition<T>[]
): TableProps.ColumnDefinition<BundleTableRow<T>>[] =>
  columnDefinitions.map((column, index) => {
    // Keep the header clickable/sortable in bundle mode. The actual ordering is applied within
    // each GTI group by BundleSortableTable (controlled sorting), so this comparator only needs
    // to exist and be well-defined; group header rows sort equal to each other.
    const itemComparator = columnItemComparator(column);
    return {
      id: column.id,
      header: column.header,
      width: column.width,
      minWidth: column.minWidth,
      maxWidth: column.maxWidth,
      isRowHeader: column.isRowHeader,
      verticalAlign: column.verticalAlign,
      hasDynamicContent: column.hasDynamicContent,
      sortingComparator: itemComparator
        ? (rowA: BundleTableRow<T>, rowB: BundleTableRow<T>) =>
            rowA.item && rowB.item ? itemComparator(rowA.item, rowB.item) : 0
        : undefined,
      cell: (row) => {
        if (row.kind === "item" && row.item) {
          return column.cell(row.item);
        }
        if (index > 0 || !row.group) {
          return null;
        }

        const { group } = row;
        const label = group.gti ? `GTI ${group.gti}` : "No GTI";
        const lineLabel = `${group.items.length} ${
          group.items.length === 1 ? "line" : "lines"
        }`;
        const usecaseLabel = `${group.usecases.length} ${
          group.usecases.length === 1 ? "usecase" : "usecases"
        }`;

        return (
          <span
            style={{
              display: "inline-block",
              whiteSpace: "nowrap",
            }}
          >
            {group.passed !== undefined && (
              <>
                <StatusIndicator type={group.passed ? "success" : "error"}>
                  {group.passed ? "PASS" : "FAIL"}
                </StatusIndicator>
                <Box variant="span" color="text-body-secondary">
                  {" "}
                  ·{" "}
                </Box>
              </>
            )}
            <Box variant="strong" display="inline">
              {label}
            </Box>
            <Box variant="span" color="text-body-secondary">
              {" "}
              · {lineLabel} · {usecaseLabel}
            </Box>
          </span>
        );
      },
    };
  });

// The subset of useCollection's propertyFilterProps that PropertyFilter consumes.
type CollectionPropertyFilterProps = {
  query: PropertyFilterProps.Query;
  onChange: PropertyFilterProps["onChange"];
  filteringProperties: readonly PropertyFilterProps.FilteringProperty[];
};

// Column-aware property filter shown above each table. Kept outside HorizontalScroll so its
// dropdown is not clipped by the scroll container. Optional `actions` (e.g. the Lines quick-filter)
// render on the same row to the left of the filter, saving a vertical row. Renders nothing when
// there is neither a filterable column nor any actions.
function ColumnFilter({
  propertyFilterProps,
  filteredItemsCount,
  totalCount,
  actions,
}: {
  propertyFilterProps: CollectionPropertyFilterProps;
  filteredItemsCount: number;
  totalCount: number;
  actions?: React.ReactNode;
}) {
  const hasFilter = propertyFilterProps.filteringProperties.length > 0;
  if (!hasFilter && !actions) {
    return null;
  }
  const countText =
    filteredItemsCount === totalCount
      ? `${totalCount} rows`
      : `${filteredItemsCount} of ${totalCount} rows`;
  const filter = hasFilter ? (
    <PropertyFilter
      {...propertyFilterProps}
      i18nStrings={FILTER_I18N_STRINGS}
      countText={
        propertyFilterProps.query.tokens.length > 0 ? countText : undefined
      }
      expandToViewport
    />
  ) : null;
  if (!actions) {
    return filter;
  }
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        flexWrap: "wrap",
      }}
    >
      {actions}
      <div style={{ flex: 1, minWidth: 220 }}>{filter}</div>
    </div>
  );
}

// Flat, sortable + filterable table. Column headers sort; the property filter narrows rows by any
// column (contains/equals). Both are driven by useCollection.
function PlainSortableTable<T>({
  items,
  columnDefinitions,
  filterActions,
  initialPropertyFilter,
  onPropertyFilterChange,
}: {
  items: T[];
  columnDefinitions: TableProps.ColumnDefinition<T>[];
  filterActions?: React.ReactNode;
  initialPropertyFilter?: PropertyFilterProps.Query;
  onPropertyFilterChange?: (query: PropertyFilterProps.Query) => void;
}) {
  const filteringProperties = useMemo(
    () => createFilteringProperties(columnDefinitions),
    [columnDefinitions]
  );
  const {
    items: visibleItems,
    collectionProps,
    propertyFilterProps,
  } = useCollection(items, {
    sorting: {},
    propertyFiltering: {
      filteringProperties,
      ...(initialPropertyFilter ? { defaultQuery: initialPropertyFilter } : {}),
    },
  });
  const wrappedPropertyFilterPropsPlain: CollectionPropertyFilterProps = {
    ...propertyFilterProps,
    onChange: (event) => {
      propertyFilterProps.onChange(event);
      onPropertyFilterChange?.(event.detail);
    },
  };
  return (
    <SpaceBetween size="xs">
      <ColumnFilter
        propertyFilterProps={wrappedPropertyFilterPropsPlain}
        filteredItemsCount={visibleItems.length}
        totalCount={items.length}
        actions={filterActions}
      />
      <HorizontalScroll>
        <Table<T>
          {...collectionProps}
          items={visibleItems}
          columnDefinitions={columnDefinitions}
          variant="embedded"
          contentDensity="compact"
          stickyHeader
        />
      </HorizontalScroll>
    </SpaceBetween>
  );
}

// Grouped (bundle) table. Filtering runs on the flat lines first (reusing useCollection's matcher),
// then the surviving lines are re-grouped by GTI so empty groups disappear. GTI groups keep their
// natural order; clicking a column header sorts the lines within each group. Cloudscape only sorts
// the top-level rows, so we sort each group's children ourselves and drive the header state.
function BundleSortableTable<T extends BundleTableItem>({
  items,
  columnDefinitions,
  filterActions,
  initialPropertyFilter,
  onPropertyFilterChange,
}: {
  items: T[];
  columnDefinitions: TableProps.ColumnDefinition<T>[];
  filterActions?: React.ReactNode;
  initialPropertyFilter?: PropertyFilterProps.Query;
  onPropertyFilterChange?: (query: PropertyFilterProps.Query) => void;
}) {
  const [expandedItems, setExpandedItems] = useState<BundleTableRow<T>[]>([]);
  const [sortingColumn, setSortingColumn] =
    useState<TableProps.SortingColumn<BundleTableRow<T>>>();
  const [isDescending, setIsDescending] = useState(false);

  const filteringProperties = useMemo(
    () => createFilteringProperties(columnDefinitions),
    [columnDefinitions]
  );
  const { items: visibleItems, propertyFilterProps } = useCollection(items, {
    propertyFiltering: {
      filteringProperties,
      ...(initialPropertyFilter ? { defaultQuery: initialPropertyFilter } : {}),
    },
  });
  const wrappedPropertyFilterPropsBundle: CollectionPropertyFilterProps = {
    ...propertyFilterProps,
    onChange: (event) => {
      propertyFilterProps.onChange(event);
      onPropertyFilterChange?.(event.detail);
    },
  };

  const groupedColumns = useMemo(
    () => createBundleColumns(columnDefinitions),
    [columnDefinitions]
  );

  const groupedRows = useMemo(() => {
    const rows = createBundleRows(groupShadowTestItemsByGti([...visibleItems]));
    if (!sortingColumn?.sortingComparator) {
      return rows;
    }
    const comparator = sortingColumn.sortingComparator;
    const direction = isDescending ? -1 : 1;
    return rows.map((row) => ({
      ...row,
      children: [...row.children].sort((a, b) => direction * comparator(a, b)),
    }));
  }, [visibleItems, sortingColumn, isDescending]);

  return (
    <SpaceBetween size="xs">
      <ColumnFilter
        propertyFilterProps={wrappedPropertyFilterPropsBundle}
        filteredItemsCount={visibleItems.length}
        totalCount={items.length}
        actions={filterActions}
      />
      <HorizontalScroll>
        <Table<BundleTableRow<T>>
          items={groupedRows}
          trackBy="id"
          columnDefinitions={groupedColumns}
          sortingColumn={sortingColumn}
          sortingDescending={isDescending}
          onSortingChange={({ detail }) => {
            setSortingColumn(detail.sortingColumn);
            setIsDescending(detail.isDescending ?? false);
          }}
          expandableRows={{
            getItemChildren: (row) => row.children,
            isItemExpandable: (row) => row.kind === "group",
            expandedItems,
            onExpandableItemToggle: ({ detail }) =>
              setExpandedItems((currentItems) =>
                detail.expanded
                  ? [...currentItems, detail.item]
                  : currentItems.filter((item) => item.id !== detail.item.id)
              ),
            getItemsCount: (row) => row.children.length,
          }}
          ariaLabels={{
            expandButtonLabel: (row) => `Expand ${row.group?.gti || "No GTI"}`,
            collapseButtonLabel: (row) =>
              `Collapse ${row.group?.gti || "No GTI"}`,
          }}
          variant="embedded"
          contentDensity="compact"
          stickyHeader
        />
      </HorizontalScroll>
    </SpaceBetween>
  );
}

export function BundleAwareTable<T extends BundleTableItem>({
  isBundle,
  items,
  columnDefinitions,
  filterActions,
  initialPropertyFilter,
  onPropertyFilterChange,
}: {
  isBundle?: boolean;
  items: T[];
  columnDefinitions: TableProps.ColumnDefinition<T>[];
  filterActions?: React.ReactNode;
  initialPropertyFilter?: PropertyFilterProps.Query;
  onPropertyFilterChange?: (query: PropertyFilterProps.Query) => void;
}) {
  if (!isBundle) {
    return (
      <PlainSortableTable
        items={items}
        columnDefinitions={columnDefinitions}
        filterActions={filterActions}
        initialPropertyFilter={initialPropertyFilter}
        onPropertyFilterChange={onPropertyFilterChange}
      />
    );
  }

  return (
    <BundleSortableTable
      items={items}
      columnDefinitions={columnDefinitions}
      filterActions={filterActions}
      initialPropertyFilter={initialPropertyFilter}
      onPropertyFilterChange={onPropertyFilterChange}
    />
  );
}

// Comparator for columns whose cell renders a numeric-but-string amount (e.g. "12.34"). Empty or
// non-numeric values sort as the smallest, so blanks group together at one end.
export const numericFieldComparator =
  <T,>(getValue: (item: T) => string | undefined) =>
  (a: T, b: T): number => {
    const parse = (value: string | undefined) => {
      const numericValue = Number.parseFloat(value ?? "");
      return Number.isNaN(numericValue)
        ? Number.NEGATIVE_INFINITY
        : numericValue;
    };
    const valueA = parse(getValue(a));
    const valueB = parse(getValue(b));
    if (valueA === valueB) {
      return 0;
    }
    return valueA - valueB;
  };
